import { useTranslation } from 'react-i18next';
import { ConfigurableTable } from 'src/common/components/ConfigurableTable/ConfigurableTable';
import { useTariffSummaryColumns } from './hooks/useTariffSummaryColumns';
import { useTariffSummary } from './hooks/useTariffSummary';
import { TextCell } from 'src/common/components/ConfigurableTable/Components/Cells/TextCell';
import { useAtom } from 'jotai';
import {
  tariffSummaryPageFilterAtom,
  tariffSummaryRowsPerPageFilterAtom,
  tariffSummarySortingAtomWithStorage,
} from 'src/atoms/filters/tariff-summary';
import { Suspense, useCallback } from 'react';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { createPortal } from 'react-dom';
import { useTariffAccountRef } from '../hooks/useTariffAccountRef';
import { TariffSummaryNoRows } from './components/TariffSummaryNoRows';
import { TariffSummarySMSUsageCell } from './components/TariffSummarySMSUsageCell';
import { TariffSummaryVoiceUsageCell } from './components/TariffSummaryVoiceUsageCell';
import { TariffSummaryDataUsageCell } from './components/TariffSummaryDataUsageCell';
import { TariffSummaryLastUpdate } from './components/TariffSummaryLastUpdate';
import { Sorting } from 'src/common/components/Filters/constants/sorting';
import { TariffSummaryColumnName } from './models/tariffSummaryModels';
import { useSelectedTariff } from '../hooks/useSelectedTariff';
import { TariffSummaryCodeCell } from './components/TariffSummaryCodeCell';
import { useNavigate } from 'react-router';
import { Routes } from 'src/Routes.constants';
import { PRODUCT_TYPE } from './api/tariffSummaryDTOModels';
import { SUBSCRIPTION_STATUS } from 'src/common/components/SubscriptionStatus/constants/subscriptionStatus';
import { TABS_ROOT_ID } from 'src/common/components/AppLayout/Components/TabsContainer';
import { ExternalRowSelectionCheckbox } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/ExternalRowSelectionCheckbox';
import { useExternalRowSelectionClearOnChange } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionClearOnChange';
import { TariffSummaryAlertCell } from './components/TariffSummaryAlertCell';
import { TariffSummaryAlertConfigurationDialog } from './components/TarifSummaryAlertConfigurarationDialog';
import { DialogComponentProps } from 'src/common/components/ConfigurableTable/models/configurableTable';
import { useIsFetching } from '@tanstack/react-query';
import { tariffSummaryQueries } from 'products/tariffSummary/Inventory/queries/tariffSummaryQueries.ts';
import { TableSkeleton } from 'common/components/Table/TableSkeleton.tsx';
import { useExternalRowSelectionApi } from 'common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionApi.tsx';

const BundleProductCodeCell = ({
  value,
  row: { productCodes, productType },
}: {
  value: string;
  row: { productCodes: string[]; productType: string };
}) => {
  return (
    <TariffSummaryCodeCell
      bundleProductCode={value}
      productType={productType}
      productCodes={productCodes}
    />
  );
};

const TariffSummaryAlertConfigurationDialogAction = (props: DialogComponentProps<any>) => {
  const data = props.data ? [props.data] : [];

  return <TariffSummaryAlertConfigurationDialog {...props} data={data} />;
};

export const TariffSummaryInventory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unSelectAll } = useExternalRowSelectionApi();
  const [rowsPerPage, setRowsPerPage] = useAtom(tariffSummaryRowsPerPageFilterAtom);
  const [sorting, setSorting] = useAtom(tariffSummarySortingAtomWithStorage);
  const [page, setPage] = useAtom(tariffSummaryPageFilterAtom);

  const accountRef = useTariffAccountRef();

  const { toggleSelectTariffId } = useSelectedTariff();
  const { data: tariffSummary } = useTariffSummary({
    accountRef: accountRef,
  });

  const isPreviousMonthFallback =
    tariffSummary.items.length > 0 && tariffSummary.items[0].activeCount === null;
  const columnConfiguration = useTariffSummaryColumns(isPreviousMonthFallback);

  useExternalRowSelectionClearOnChange({ rows: tariffSummary.items });

  const onPaginationChange = useCallback(
    (pagination: GridPaginationModel) => {
      if (pagination.page !== page) {
        unSelectAll();
        setPage(pagination.page);
      }

      if (pagination.pageSize !== rowsPerPage) {
        setRowsPerPage(pagination.pageSize);
      }
    },
    [page, rowsPerPage, setPage, setRowsPerPage, unSelectAll],
  );

  const onSortModelChange = useCallback((model: GridSortModel) => {
    const [sortField] = model;
    if (!sortField) {
      return;
    }

    setSorting({
      columnName: sortField.field as TariffSummaryColumnName,
      sort: sortField.sort as Sorting,
    });
  }, []);

  const handleRedirect = (productType: string, productCodes: string[]) => {
    const filters = `?productCodes=${productCodes?.join(',')}&subscriptionStatus=${
      SUBSCRIPTION_STATUS.ACTIVE
    }&accounts=${accountRef}`;
    if (productType === PRODUCT_TYPE.RSIM) {
      const redirectToURl = Routes.productsRsim + filters;
      navigate(redirectToURl);
    } else if (productType === PRODUCT_TYPE.SIM) {
      const redirectToURl = Routes.productsSim + filters + '&columnName=liveDataUsage&sort=desc';
      navigate(redirectToURl);
    }
  };

  const tabsRoot = document.getElementById(TABS_ROOT_ID);

  const isFetching =
    useIsFetching({
      queryKey: tariffSummaryQueries.listAll(),
    }) > 0;

  return isFetching ? (
    <TableSkeleton showEndSkeleton />
  ) : (
    <>
      {tabsRoot &&
        createPortal(
          <Suspense fallback={''}>
            <TariffSummaryLastUpdate />
          </Suspense>,
          tabsRoot,
        )}
      <ConfigurableTable
        getRowId={(row) => row.bundleProductCode}
        configuration={columnConfiguration}
        rows={tariffSummary.items}
        page={page}
        pageSize={rowsPerPage}
        onPaginationChange={onPaginationChange}
        totalNumberOfItems={tariffSummary.totalNumberOfItems}
        cellConfiguration={{
          'select-action': ExternalRowSelectionCheckbox,
          default: TextCell,
          bundleProductCode: BundleProductCodeCell,
          alertConfiguration: TariffSummaryAlertCell,
          sent: TariffSummarySMSUsageCell,
          usedData: TariffSummaryDataUsageCell,
          usedSeconds: TariffSummaryVoiceUsageCell,
        }}
        slots={{
          noRowsOverlay: TariffSummaryNoRows,
        }}
        sort={sorting}
        onSortModelChange={onSortModelChange}
        disableRowSelectionOnClick={false}
        onRowSelect={(tariffId) => toggleSelectTariffId(tariffId)}
        actions={[
          {
            label: t('tariffSummary.table.viewDetails'),
            closable: true,
            actionCallback: (data) => {
              handleRedirect(data.productType, data.productCodes);
            },
            enabled: (data) => {
              return data.productType && data.productCodes.length;
            },
          },

          {
            id: 'setUpAlert',
            label: t('tariffSummary.table.usageAlert.setUpAlert'),
            type: 'dialog',
            Component: TariffSummaryAlertConfigurationDialogAction,
            enabled: true,
          },
        ]}
      />
    </>
  );
};
